import * as Sentry from '@sentry/react'
import type { PaymentIntent, SetupIntent } from '@stripe/stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { defer } from 'react-router-dom'
import createRevenueCatPurchase from '../helpers/createRevenueCatPurchase'
import {
  CUSTOM_PURCHASE_EVENT_KEY,
  trackSubscriptionEvent,
} from '../helpers/facebookTracking'
import {
  createStripeTrialSubscription,
  getUser,
  grantRevenueCatEntitlement,
  scheduleRevenueCatEmailUpdate,
} from '../helpers/supabase'
import { Plan } from '../types'

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string,
)

export default function completePaymentLoader({
  request,
}: {
  request: Request
}) {
  const completePaymentPromise = completePayment(request)

  return defer({
    completed: completePaymentPromise,
  })
}

async function completePayment(request: Request) {
  const url = new URL(request.url)

  const paymentIntentClientSecret = url.searchParams.get(
    'payment_intent_client_secret',
  )
  const setupIntentClientSecret = url.searchParams.get(
    'setup_intent_client_secret',
  )
  const isTrial = Boolean(setupIntentClientSecret)
  const plan = url.searchParams.get('plan') as Plan | null
  const stripeFirstPriceId = url.searchParams.get('stripe_first_price_id')!
  const stripeNextPriceId = url.searchParams.get('stripe_next_price_id')!
  const subscriptionId = url.searchParams.get('subscription_id')

  const currency = url.searchParams.get('currency')

  let paymentIntent: PaymentIntent | undefined
  let setupIntent: SetupIntent | undefined

  if (setupIntentClientSecret) {
    setupIntent = await retrieveStripeSetupIntent(setupIntentClientSecret)
    if (!setupIntent || setupIntent.status !== 'succeeded') return false
  } else if (paymentIntentClientSecret) {
    paymentIntent = await retrieveStripePaymentIntent(paymentIntentClientSecret)
    if (!paymentIntent || paymentIntent.status !== 'succeeded') return false
  } else {
    return false
  }

  // getUser already retries token-lock failures internally, which is what this
  // path needs: the payment has succeeded server-side by the time the loader
  // runs, so a transient lock hiccup must not dump the user on the error page.
  const {
    data: { user: supabaseUser },
  } = await getUser()

  if (!supabaseUser) return false

  const isOneTime = plan === 'one-time'

  if (isOneTime) {
    if (!paymentIntent) return false

    try {
      await grantRevenueCatEntitlement({ paymentIntentId: paymentIntent.id })
    } catch (error) {
      // One-time purchases have no webhook fallback — if the grant fails the
      // customer has been charged without receiving their entitlement. Report
      // and rethrow so <Await errorElement> shows the error page instead of
      // redirecting to the success page.
      Sentry.captureException(error)
      throw error
    }
  } else if (isTrial && setupIntent) {
    // The trial subscription is created only now — after the card was saved —
    // so the trial does not start when the user merely reaches the payment
    // page. Failure here means we have a saved card but no trial, so surface
    // the error page instead of a false success.
    try {
      const { data } = await createStripeTrialSubscription({
        setupIntentId: setupIntent.id,
      })
      await createRevenueCatPurchase({
        subscriptionId: data.subscriptionId,
        userId: supabaseUser.id,
      })
    } catch (error) {
      Sentry.captureException(error)
      throw error
    }
  } else if (subscriptionId) {
    await createRevenueCatPurchase({
      subscriptionId,
      userId: supabaseUser.id,
    })
  }

  try {
    await scheduleRevenueCatEmailUpdate()
  } catch (error) {
    Sentry.captureException(error)
  }

  const customPurchaseEventName =
    localStorage.getItem(CUSTOM_PURCHASE_EVENT_KEY) ?? undefined
  if (customPurchaseEventName) {
    localStorage.removeItem(CUSTOM_PURCHASE_EVENT_KEY)
  }

  await trackSubscriptionEvent({
    customPurchaseEventName,
    currency: paymentIntent?.currency ?? currency ?? 'eur',
    eventType: 'purchase',
    stripeFirstPriceId,
    stripeNextPriceId,
  })

  return true
}

async function retrieveStripePaymentIntent(
  clientSecret: string,
): Promise<PaymentIntent | undefined> {
  const stripe = await stripePromise

  const response = await stripe?.retrievePaymentIntent(clientSecret)

  return response?.paymentIntent
}

async function retrieveStripeSetupIntent(
  clientSecret: string,
): Promise<SetupIntent | undefined> {
  const stripe = await stripePromise
  const response = await stripe?.retrieveSetupIntent(clientSecret)

  return response?.setupIntent
}
